import { Button, DatePicker } from '@consigli/facade';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { getMeetingId, type SupportedItem } from './action-item-types';
import { formatDateToLocale } from './action-items-table-utils';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';

interface EditableDeadlineProps {
  item: SupportedItem;
  meetingId?: string;
  onUpdate?: (updated: SupportedItem) => void;
}

export const EditableDeadline: FC<EditableDeadlineProps> = ({
  item,
  meetingId: propMeetingId,
  onUpdate,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedMeetingId = getMeetingId(item, propMeetingId);
  const { updateItem } = useActionItemMutations(resolvedMeetingId);

  const [isEditing, setIsEditing] = useState(false);
  const [editDate, setEditDate] = useState('');

  const inputRef = useCallback((el: HTMLInputElement | null) => {
    el?.focus();
  }, []);

  const startEditing = () => {
    setIsEditing(true);
    // Convert existing date to YYYY-MM-DD format for input[type="date"]
    // Just extract the date portion directly from the ISO string to avoid timezone issues
    if (item.dueDate) {
      setEditDate(item.dueDate.substring(0, 10));
    } else {
      setEditDate('');
    }
  };

  const commitEdit = async () => {
    // Compare date portions only (YYYY-MM-DD) to avoid unnecessary updates
    // when times differ but dates are the same
    const currentDatePart = item.dueDate ? item.dueDate.substring(0, 10) : null;
    // Normalize empty string to null for comparison
    const newDatePart = editDate || null;

    // Only update if the date actually changed
    if (newDatePart !== currentDatePart) {
      // Use T00:00:00 without Z suffix to avoid timezone shifts
      // Backend stores dates without timezone info
      const newDueDate = editDate ? `${editDate}T00:00:00` : null;
      try {
        await updateItem(item.id, {
          dueDate: newDueDate,
        });
        onUpdate?.({ ...item, dueDate: newDueDate } as SupportedItem);
        toast.success(t('meeting-secretary.action-items.item-updated'));
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-item'));
      }
    }
    setIsEditing(false);
    setEditDate('');
  };

  const clearDate = async () => {
    if (item.dueDate) {
      try {
        await updateItem(item.id, {
          dueDate: null,
        });
        onUpdate?.({ ...item, dueDate: null } as SupportedItem);
        toast.success(t('meeting-secretary.action-items.item-updated'));
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-item'));
      }
    }
    setIsEditing(false);
    setEditDate('');
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditDate('');
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <DatePicker
          ref={inputRef}
          variant="compact"
          value={editDate}
          onChange={(e) => setEditDate(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              cancelEdit();
            }
          }}
        />
        {item.dueDate && (
          <Button
            tertiary
            iconOnly
            icon={FaTimes}
            iconSize={12}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearDate();
            }}
            title={t('meeting-secretary.action-items.clear-deadline', 'Clear deadline')}
            className="!p-0.5 text-gray-400 hover:text-error-dark"
          />
        )}
      </div>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        startEditing();
      }}
      className="text-left text-gray-600 hover:text-gray-900 hover:underline transition-colors cursor-pointer"
      title={
        item.dueDate
          ? formatDateToLocale(item.dueDate, i18n.language)
          : t('meeting-secretary.action-items.no-deadline', 'No Deadline')
      }
    >
      {item.dueDate ? formatDateToLocale(item.dueDate, i18n.language) : '—'}
    </button>
  );
};
